// Add Context Card sheet — three modes (search published cards, generate
// with Claude, create manually). Mirrors the mobile NarrativeContextCard-
// AddButton flow. Uses POST /news/editor/context-cards/draft for AI
// generation and POST /news/editor/context-cards for create, then attaches
// the new card via POST /news/editor/articles/:articleId/context-cards.

import { useState } from "react";
import { Button } from "./ui/Button";
import { Card, CardSubtle } from "./ui/Card";
import { Input, Label, Textarea } from "./ui/Input";
import { Modal } from "./ui/Modal";
import { Spinner } from "./ui/Spinner";
import { api } from "../lib/api";

interface AddContextCardSheetProps {
  open: boolean;
  articleId: string;
  onClose: () => void;
  onAttached: () => void;
}

interface SearchResult {
  id: string;
  title: string;
  category: string;
  slug: string;
}

interface DraftShape {
  category: string;
  title: string;
  slug: string;
  body: string;
  key_entities: string[];
  suggested_external_url: string | null;
  suggested_external_label: string | null;
}

const EMPTY_DRAFT: DraftShape = {
  category: "",
  title: "",
  slug: "",
  body: "",
  key_entities: [],
  suggested_external_url: null,
  suggested_external_label: null,
};

type Mode = "search" | "draft-prompt" | "draft-edit" | "manual";

export function AddContextCardSheet({
  open,
  articleId,
  onClose,
  onAttached,
}: AddContextCardSheetProps) {
  const [mode, setMode] = useState<Mode>("search");
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const [draftPrompt, setDraftPrompt] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [draft, setDraft] = useState<DraftShape | null>(null);
  const [saving, setSaving] = useState(false);

  function reset() {
    setMode("search");
    setSearchQuery("");
    setSearchResults([]);
    setDraftPrompt("");
    setDraft(null);
    setError(null);
  }

  function close() {
    reset();
    onClose();
  }

  async function runSearch(q: string) {
    setSearchQuery(q);
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const r = await api<{ cards: SearchResult[] }>(
        `/news/editor/context-cards?status=published&search=${encodeURIComponent(
          q.trim(),
        )}`,
      );
      setSearchResults(r.cards ?? []);
    } catch (e: any) {
      setError(e.message ?? "Search failed");
    } finally {
      setSearching(false);
    }
  }

  async function attachExisting(cardId: string) {
    setError(null);
    try {
      await api(`/news/editor/articles/${articleId}/context-cards`, {
        method: "POST",
        body: { context_card_id: cardId },
      });
      onAttached();
      close();
    } catch (e: any) {
      setError(e.message ?? "Failed to attach card");
    }
  }

  async function generateDraft() {
    if (!draftPrompt.trim()) return;
    setError(null);
    setDraftLoading(true);
    try {
      const r = await api<{ draft: DraftShape }>(
        "/news/editor/context-cards/draft",
        { method: "POST", body: { prompt: draftPrompt.trim() } },
      );
      setDraft(r.draft);
      setMode("draft-edit");
    } catch (e: any) {
      setError(e.message ?? "Failed to draft card");
    } finally {
      setDraftLoading(false);
    }
  }

  async function saveCard(publish: boolean) {
    if (!draft) return;
    if (!draft.title || !draft.slug || !draft.body) {
      setError("Title, slug, and body are required");
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const created = await api<{ id: string }>("/news/editor/context-cards", {
        method: "POST",
        body: {
          slug: draft.slug,
          category: draft.category,
          title: draft.title,
          body: draft.body,
          key_entities: draft.key_entities ?? [],
          external_url: draft.suggested_external_url ?? null,
          external_label: draft.suggested_external_label ?? null,
          status: publish ? "published" : "draft",
        },
      });
      if (publish) {
        await api(`/news/editor/articles/${articleId}/context-cards`, {
          method: "POST",
          body: { context_card_id: created.id },
        });
        onAttached();
      }
      close();
    } catch (e: any) {
      setError(e.message ?? "Failed to save card");
    } finally {
      setSaving(false);
    }
  }

  const sheetTitle =
    mode === "search"
      ? "Add Context Card"
      : mode === "draft-prompt"
        ? "Generate with AI"
        : mode === "draft-edit"
          ? "Review & Save Card"
          : "Create Manually";

  return (
    <Modal open={open} onClose={close} title={sheetTitle} width={720}>
      {error ? (
        <Card style={{ marginBottom: 12, padding: 12 }}>
          <CardSubtle style={{ color: "var(--danger)" }}>{error}</CardSubtle>
        </Card>
      ) : null}

      {mode === "search" ? (
        <>
          <Label>Search published cards</Label>
          <Input
            value={searchQuery}
            onChange={(e) => runSearch(e.target.value)}
            placeholder="e.g. anglo-egyptian, gerd, entebbe…"
            autoFocus
          />
          <div style={{ marginTop: 12, minHeight: 60 }}>
            {searching ? (
              <div style={{ textAlign: "center", padding: 12 }}>
                <Spinner size={16} />
              </div>
            ) : searchResults.length === 0 ? (
              searchQuery.trim() ? (
                <CardSubtle>No matching published cards.</CardSubtle>
              ) : (
                <CardSubtle>
                  Type to search the published context-card library.
                </CardSubtle>
              )
            ) : (
              searchResults.map((r) => (
                <button
                  key={r.id}
                  onClick={() => attachExisting(r.id)}
                  style={styles.resultRow}
                >
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <div style={{ fontWeight: 600 }}>{r.title}</div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      {r.category}
                    </div>
                  </div>
                  <span style={{ color: "var(--accent)", fontSize: 13 }}>
                    Attach
                  </span>
                </button>
              ))
            )}
          </div>

          <div style={styles.divider}>
            <div style={styles.dividerLine} />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>or</span>
            <div style={styles.dividerLine} />
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              onClick={() => setMode("draft-prompt")}
              style={{ flex: 1 }}
            >
              ✨ Create new with AI
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setDraft({ ...EMPTY_DRAFT });
                setMode("manual");
              }}
              style={{ flex: 1 }}
            >
              ✏️ Create manually
            </Button>
          </div>
        </>
      ) : mode === "draft-prompt" ? (
        <>
          <Label>What do you need?</Label>
          <Textarea
            value={draftPrompt}
            onChange={(e) => setDraftPrompt(e.target.value)}
            rows={5}
            placeholder='e.g. "Explain the 1929 Anglo-Egyptian Treaty and Britain&apos;s historical control of the Nile"'
            autoFocus
            disabled={draftLoading}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Button
              variant="secondary"
              onClick={() => setMode("search")}
              disabled={draftLoading}
            >
              ← Back
            </Button>
            <Button
              onClick={generateDraft}
              disabled={draftLoading || !draftPrompt.trim()}
              style={{ flex: 1 }}
            >
              {draftLoading ? "Generating…" : "Generate draft"}
            </Button>
          </div>
        </>
      ) : draft ? (
        <>
          <Label>Category</Label>
          <Input
            value={draft.category}
            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
            style={{ marginBottom: 12 }}
          />
          <Label>Title</Label>
          <Input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            style={{ marginBottom: 12 }}
          />
          <Label>Slug</Label>
          <Input
            value={draft.slug}
            onChange={(e) => setDraft({ ...draft, slug: e.target.value })}
            style={{ marginBottom: 12 }}
          />
          <Label>Body (markdown)</Label>
          <Textarea
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            rows={12}
            style={{
              marginBottom: 12,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 13,
              lineHeight: 1.6,
            }}
          />
          <Label>External URL (optional)</Label>
          <Input
            value={draft.suggested_external_url ?? ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                suggested_external_url: e.target.value.trim() || null,
              })
            }
            placeholder="https://..."
            style={{ marginBottom: 12 }}
          />
          <Label>External link label (optional)</Label>
          <Input
            value={draft.suggested_external_label ?? ""}
            onChange={(e) =>
              setDraft({
                ...draft,
                suggested_external_label: e.target.value.trim() || null,
              })
            }
            placeholder="e.g. Read the full treaty"
            style={{ marginBottom: 16 }}
          />

          <div style={{ display: "flex", gap: 8 }}>
            <Button
              variant="secondary"
              onClick={() => setMode("search")}
              disabled={saving}
            >
              ← Back
            </Button>
            <Button
              variant="secondary"
              onClick={() => saveCard(false)}
              disabled={saving}
              style={{ flex: 1 }}
            >
              Save as draft
            </Button>
            <Button
              onClick={() => saveCard(true)}
              disabled={saving}
              style={{ flex: 1 }}
            >
              {saving ? "Saving…" : "Save & attach"}
            </Button>
          </div>
        </>
      ) : null}
    </Modal>
  );
}

const styles: Record<string, React.CSSProperties> = {
  resultRow: {
    display: "flex",
    width: "100%",
    alignItems: "center",
    gap: 8,
    padding: "10px 12px",
    background: "transparent",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-sm)",
    cursor: "pointer",
    marginBottom: 6,
    color: "var(--text-primary)",
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    margin: "16px 0",
  },
  dividerLine: {
    flex: 1,
    height: 1,
    background: "var(--border)",
  },
};
